import { Controller } from "@hotwired/stimulus"

// Chip/token editor. Tags live in `this.tags` and are mirrored into a single
// hidden field as a comma-joined string. When `suggestions` are provided, the
// dropdown is built entirely here from that list — filtered by the current
// query, with already-selected tags excluded, and arrow-key navigation.
export default class extends Controller {
  static targets = ["input", "hidden", "chips", "dropdown"]
  static values = {
    suggestions: { type: Array, default: [] },
    placeholder: { type: String, default: "Add tag…" }
  }

  connect() {
    this.tags = this.parse(this.hiddenTarget.value)
    this.activeIndex = -1
    this.visible = []
    this._onDocClick = this.onDocumentClick.bind(this)
    document.addEventListener("click", this._onDocClick)
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick)
  }

  // --- input events -------------------------------------------------------

  keydown(event) {
    switch (event.key) {
      case "Enter":
      case ",":
        event.preventDefault()
        if (this.activeIndex >= 0 && this.visible[this.activeIndex]) {
          this.addTag(this.visible[this.activeIndex])
        } else {
          this.addTag(this.inputTarget.value)
        }
        break
      case "ArrowDown":
        if (this.hasSuggestions) { event.preventDefault(); this.move(1) }
        break
      case "ArrowUp":
        if (this.hasSuggestions) { event.preventDefault(); this.move(-1) }
        break
      case "Escape":
        this.close()
        break
      case "Backspace":
        if (this.inputTarget.value === "") this.removeLast()
        break
    }
  }

  search() {
    this.open()
  }

  open() {
    if (!this.hasSuggestions) return
    this.renderOptions()
    this.dropdownTarget.classList.remove("hidden")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  close() {
    if (!this.hasDropdownTarget) return
    this.dropdownTarget.classList.add("hidden")
    this.activeIndex = -1
    this.inputTarget.setAttribute("aria-expanded", "false")
  }

  // Refocus the input when the surrounding chrome is clicked, but not when a
  // chip's remove button was the target.
  focusInput(event) {
    if (event.target.closest("button")) return
    this.inputTarget.focus()
  }

  // --- mutations ----------------------------------------------------------

  addTag(raw, { reopen = true } = {}) {
    const name = String(raw).trim().replace(/,+$/, "").trim()
    if (!name) return
    if (!this.has(name)) {
      this.tags.push(name)
      this.renderChips()
      this.sync()
    }
    this.inputTarget.value = ""
    if (reopen && this.dropdownOpen) this.renderOptions()
  }

  remove(event) {
    this.removeTag(event.params.tag)
  }

  removeTag(name) {
    this.tags = this.tags.filter(t => t !== name)
    this.renderChips()
    this.sync()
    if (this.dropdownOpen) this.renderOptions()
  }

  removeLast() {
    if (this.tags.length === 0) return
    this.removeTag(this.tags[this.tags.length - 1])
  }

  // --- suggestion dropdown ------------------------------------------------

  // Pick the suggestion at the given index (set via data-index on the row).
  pick(event) {
    const tag = event.currentTarget.dataset.tag
    this.addTag(tag)
    this.inputTarget.focus()
  }

  // Highlight the row under the cursor so mouse and keyboard agree.
  hover(event) {
    this.activeIndex = Number(event.currentTarget.dataset.index)
    this.paintActive()
  }

  move(step) {
    if (this.visible.length === 0) return
    const count = this.visible.length
    // From "nothing highlighted", ArrowDown goes to the first row and ArrowUp
    // wraps to the last, instead of landing somewhere in the middle.
    const start = this.activeIndex === -1 ? (step > 0 ? -1 : 0) : this.activeIndex
    this.activeIndex = (start + step + count) % count
    this.paintActive()
    this.scrollActiveIntoView()
  }

  renderOptions() {
    const query = this.inputTarget.value.trim().toLowerCase()
    this.visible = this.suggestionsValue.filter(name =>
      !this.has(name) && name.toLowerCase().includes(query)
    )
    // Nothing is highlighted by default: Enter adds exactly what was typed.
    // The user must arrow into the list to pick a suggestion instead.
    this.activeIndex = -1

    if (this.visible.length === 0) {
      this.dropdownTarget.innerHTML =
        `<li class="px-3 py-2 text-sm text-base-content/50 pointer-events-none">No suggestions</li>`
      return
    }

    this.dropdownTarget.innerHTML = this.visible
      .map((name, i) => `
        <li>
          <a class="transition-none ${i === this.activeIndex ? "menu-active" : ""}"
             role="option"
             data-index="${i}"
             data-tag="${this.attr(name)}"
             data-action="mousedown->tag-input#preventBlur click->tag-input#pick mousemove->tag-input#hover">
            ${this.html(name)}
          </a>
        </li>
      `)
      .join("")
  }

  paintActive() {
    this.dropdownTarget.querySelectorAll("a[role=option]").forEach(el => {
      el.classList.toggle("menu-active", Number(el.dataset.index) === this.activeIndex)
    })
  }

  scrollActiveIntoView() {
    const el = this.dropdownTarget.querySelector(`a[data-index="${this.activeIndex}"]`)
    if (el) el.scrollIntoView({ block: "nearest" })
  }

  // Stop the input from blurring (and the dropdown from closing) before the
  // click on a suggestion row registers.
  preventBlur(event) {
    event.preventDefault()
  }

  // --- chips --------------------------------------------------------------

  renderChips() {
    this.chipsTarget.innerHTML = this.tags
      .map(tag => `
        <span class="badge badge-neutral gap-1 select-none transition-none">
          <span>${this.html(tag)}</span>
          <button type="button" class="cursor-pointer opacity-60 hover:opacity-100 transition-none"
            data-action="click->tag-input#remove"
            data-tag-input-tag-param="${this.attr(tag)}">
            <span class="text-base leading-none">&times;</span>
          </button>
        </span>
      `)
      .join("")
    this.inputTarget.placeholder = this.tags.length ? "" : this.placeholderValue
  }

  // --- helpers ------------------------------------------------------------

  sync() {
    this.hiddenTarget.value = this.tags.join(",")
  }

  parse(value) {
    return value ? value.split(",").map(t => t.trim()).filter(Boolean) : []
  }

  has(name) {
    const key = name.toLowerCase()
    return this.tags.some(t => t.toLowerCase() === key)
  }

  get hasSuggestions() {
    return this.hasDropdownTarget && this.suggestionsValue.length > 0
  }

  get dropdownOpen() {
    return this.hasDropdownTarget && !this.dropdownTarget.classList.contains("hidden")
  }

  onDocumentClick(event) {
    if (this.element.contains(event.target)) return
    const pending = this.inputTarget.value.trim()
    if (pending) this.addTag(pending, { reopen: false })
    this.close()
  }

  html(str) {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
  }

  attr(str) {
    return str.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/'/g, "&#39;")
  }
}
